import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Loader2, Mail, Phone } from "lucide-react";
import { toast } from "sonner";
import DynamicForm from "@/components/forms/DynamicForm";
import { api } from "@/lib/api";

const EASE = [0.16, 1, 0.3, 1];

const DEFAULTS = {
  contact_heading: "A journey of a thousand miles starts with a single step.",
  contact_subcopy: "Whether you're in school or growing your professional presence, AB Hunter Consulting USA is ready to help you achieve everything you desire. B.E.T with AB Hunter.",
  contact_phone: "225.603.4116",
  contact_email: "engage@abhunter.us",
};

export default function ContactSection() {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [c, setC] = useState(DEFAULTS);

  useEffect(() => {
    api.get("/forms?purpose=contact")
      .then(({ data }) => setForm(data[0] || null))
      .catch(() => toast.error("Could not load the contact form."))
      .finally(() => setLoading(false));
    api.get("/content")
      .then(({ data }) => setC({ ...DEFAULTS, ...data }))
      .catch(() => {});
  }, []);

  const tel = c.contact_phone.replace(/[^0-9+]/g, "");

  return (
    <section id="contact" data-testid="contact-section" className="hero-glow relative border-t border-gold/10 py-28 sm:py-36">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-14 px-5 sm:px-8 lg:grid-cols-2">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: EASE }}
        >
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-gold">Book a Visit</span>
          <h2 className="mt-4 font-display text-3xl font-semibold leading-tight tracking-tight sm:text-4xl lg:text-5xl">
            {c.contact_heading}
          </h2>
          <p className="mt-6 max-w-md text-base leading-relaxed text-muted-foreground">
            {c.contact_subcopy}
          </p>

          <div className="mt-10 space-y-4">
            <a href={`tel:${tel}`} data-testid="contact-phone-link" className="group flex items-center gap-4 rounded-2xl border border-gold/15 bg-ink-card/60 p-5 transition-all duration-300 hover:border-gold/50">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-gold/10 text-gold transition-all duration-300 group-hover:bg-gold group-hover:text-ink">
                <Phone className="h-5 w-5" />
              </span>
              <span>
                <span className="block font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Call</span>
                <span className="block font-display text-lg font-semibold">{c.contact_phone}</span>
              </span>
            </a>
            <a href={`mailto:${c.contact_email}`} data-testid="contact-email-link" className="group flex items-center gap-4 rounded-2xl border border-gold/15 bg-ink-card/60 p-5 transition-all duration-300 hover:border-gold/50">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-gold/10 text-gold transition-all duration-300 group-hover:bg-gold group-hover:text-ink">
                <Mail className="h-5 w-5" />
              </span>
              <span>
                <span className="block font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Email</span>
                <span className="block font-display text-lg font-semibold">{c.contact_email}</span>
              </span>
            </a>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, delay: 0.15, ease: EASE }}
          className="rounded-3xl border border-gold/20 bg-ink-card/70 p-7 backdrop-blur sm:p-10"
        >
          <h3 className="font-display text-xl font-semibold">Book your consultation now</h3>
          <div className="mt-6" data-testid="contact-form">
            {loading ? (
              <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-gold" /></div>
            ) : form ? (
              <DynamicForm form={form} testIdPrefix="contact" />
            ) : (
              <p className="py-8 text-center text-sm text-muted-foreground">Form unavailable — please call {c.contact_phone} or email {c.contact_email}.</p>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
